import { AnimationEvent, MouseEvent, useEffect, useRef, useState } from "react"
import { GetApp } from "../../App"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { classNames } from "../../Components"
import { EditGroup } from "../../Components/EditGroup"
import { Icon, Icons } from "../../Components/Icon"
import { LoginSignUpViewProps } from "./Params"

type LoginSignUpFocusField = "accountName" | "name" | "email" | "password";

export function LoginSignUpView({ active, onActivate, edited, onEdited, saveEdits, validateEdits, signupError, showPassword, setShowPassword, validInput, loading, invitationData, invitationToken }: LoginSignUpViewProps) {
  const app = GetApp()!;
  const t = app.getT("LoginView");

  const accountNameRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  // Tracks each non-password field as the visitor types it, purely so the box's
  // placeholder (shown while the *other* box is active -- see LoginView, which only
  // ever keeps one box's real <form>/EditGroup mounted) still looks like the fields
  // it's mimicking instead of always rendering blank, and so reactivating restores them
  // into the real inputs. The password is intentionally never captured here: unlike
  // partially-typed text, it has no legitimate reason to be echoed back in a plain,
  // unmasked <div>.
  //
  // Each only ever holds what the visitor themselves typed (never seeded from
  // invitationData): invitationData is fetched asynchronously by LoginView (see the
  // signup-invitation flow), so it can arrive as a *later* prop update on this
  // already-mounted box. Deriving the displayed value at render time (falling back to
  // invitationData only while the visitor hasn't typed anything) means the inputs pick
  // up a late-arriving invitation correctly, the same way the plain
  // `defaultValue={invitationData?.name/email}` expressions they replace did.
  const [accountName, setAccountName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const displayName = name || (invitationData && invitationData.name) || "";
  const displayEmail = email || (invitationData && invitationData.email) || "";

  const accountNameRequired = signupError === "signupAccountAlreadyExists";

  // Remembers which field (if any) the visitor clicked on this box's inactive
  // placeholder -- set by the field-specific handlers below -- so the effect below can
  // focus the corresponding real input once the click also activates this box.
  // `undefined` means the click landed elsewhere in the box (its background, a label,
  // the submit button) rather than on a specific field's placeholder, so the effect
  // falls back to focusing the first empty field instead.
  const pendingFocusFieldRef = useRef<LoginSignUpFocusField | undefined>(undefined);
  // Tracks whether this box was active on the previous render, so the focus-on-
  // activation effect below only fires on a real inactive -> active transition (a
  // visitor's click) -- never on initial mount, where this box may already start
  // active, and autofocusing on page load with no user interaction would be an
  // unwanted UX regression.
  const wasActiveRef = useRef(active);

  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = active;
    if (!active || wasActive) {
      return;
    }
    const target = pendingFocusFieldRef.current;
    pendingFocusFieldRef.current = undefined;
    if (target === "accountName") {
      accountNameRef.current?.focus();
    } else if (target === "name") {
      nameRef.current?.focus();
    } else if (target === "email") {
      emailRef.current?.focus();
    } else if (target === "password") {
      passwordRef.current?.focus();
    } else if (accountNameRequired && !accountName) {
      // A click elsewhere in the box (not on a specific field): focus the first empty
      // field, in the same top-to-bottom order the fields are laid out. Password is
      // always empty on a fresh activation, since it's never mirrored into the
      // placeholder, so it's the last resort.
      accountNameRef.current?.focus();
    } else if (!displayName) {
      nameRef.current?.focus();
    } else if (!displayEmail) {
      emailRef.current?.focus();
    } else {
      passwordRef.current?.focus();
    }
  }, [active, accountNameRequired, accountName, displayName, displayEmail]);

  const activateField = (field: LoginSignUpFocusField) => (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    pendingFocusFieldRef.current = field;
    onActivate();
  };

  // Browser autofill (password managers, saved-form-data, address/email suggestions) can
  // set an input's value without dispatching a React-visible `input`/`change` event, so
  // `onEdited` (wired to onChange below) never fires and the submit button stays disabled
  // even though the fields are visibly filled in. The `:autofill`/`:-webkit-autofill` CSS
  // rule in LoginView.scss triggers a (near-instant, invisible) animation on autofill,
  // which browsers reliably surface as a real `animationstart` DOM event even when
  // `input`/`change` are suppressed -- so treat that the same as an edit.
  //
  // Validate directly off each field's live DOM ref here, rather than relying solely on
  // EditGroup's `changed`-triggered effect to re-read a `FormData` snapshot on a later
  // render: that path can miss or race a just-autofilled value (e.g. autofilling one
  // field right after manually typing the others), leaving the button stuck disabled.
  // Reading the refs directly guarantees every field's current value is picked up,
  // regardless of fill order.
  const handleAutofill = (e: AnimationEvent<HTMLInputElement>) => {
    if (e.animationName !== "onAutoFillStart") {
      return;
    }
    const values = {
      accountName: accountNameRef.current?.value || undefined,
      name: nameRef.current?.value || "",
      email: emailRef.current?.value || "",
      password: passwordRef.current?.value || "",
    };
    setAccountName(values.accountName ?? "");
    setName(values.name);
    setEmail(values.email);
    onEdited();
    validateEdits(new FormData(), values);
  };

  return (
    <div className={classNames(["signup", !active && "inactive"])} onClick={active ? undefined : onActivate}>
      <h4 className="text_on_box">{t("signup")}</h4>

      {/* <button className="button signup-button">Signup with Apple</button>
      <button className="button signup-button">Signup with Google</button> */}

      <Block className="signup-block">
        <BlockRow><p>{t("signup_email")}</p></BlockRow>
        {active ? (
          <EditGroup changed={edited} editable={true} editableFields={["accountName", "name", "email", "password"]} manualSave={true} onSave={saveEdits} onValidate={validateEdits}>
            {signupError && <div className="LoginView-error">{t(`error_${signupError}`)}</div>}

            {accountNameRequired && (
              <BlockRow>
                <BlockField label={t("form_account_name")} labelFor={"signup_accountName"}>
                  <input ref={accountNameRef} id="signup_accountName" name="accountName" type="name" autoComplete="organization" onChange={(e) => { setAccountName(e.target.value); onEdited(); }} onAnimationStart={handleAutofill} defaultValue={accountName} />
                </BlockField>
              </BlockRow>
            )}

            <BlockRow>
              <BlockField label={t("form_name")} labelFor={"signup_name"}>
                <input ref={nameRef} id="signup_name" name="name" type="name" autoComplete="name" onChange={(e) => { setName(e.target.value); onEdited(); }} onAnimationStart={handleAutofill} defaultValue={displayName} readOnly={!!invitationToken} />
              </BlockField>
            </BlockRow>

            <BlockRow>
              <BlockField label={t("form_email")} labelFor={"signup_email"}>
                <input ref={emailRef} id="signup_email" name="email" type="email" autoComplete="email" onChange={(e) => { setEmail(e.target.value); onEdited(); }} onAnimationStart={handleAutofill} defaultValue={displayEmail} readOnly={!!invitationToken} />
              </BlockField>
            </BlockRow>

            <BlockRow>
              <BlockField label={t("form_password")} labelFor={"signup_password"}>
                <span className="password-field">
                  <input ref={passwordRef} id="signup_password" name="password" type={showPassword ? "text" : "password"} autoComplete="new-password" onChange={onEdited} onAnimationStart={handleAutofill} />
                  <Icon icon={showPassword ? Icons.EyeOn : Icons.EyeOff} className="password-toggle" onClick={() => setShowPassword(!showPassword)} />
                </span>
              </BlockField>
            </BlockRow>

            <BlockRow className="row-right block-row-top0">
              <button className="button submit-button" disabled={!validInput || loading} type="submit">{t("form_submit_signup")}</button>

              {/* TODO:  when invitation: no email for verification needed */}

            </BlockRow>
          </EditGroup>
        ) : (
          // Inactive: no real <form>/EditGroup/<input> -- just look-alike placeholders
          // (see `.input` in Common.scss) so this box can never contribute a second,
          // real email/password field to the DOM alongside the login box's. Each
          // placeholder still calls `onActivate` (via `activateField`) when clicked, so
          // clicking anywhere in the box still activates it -- but stops the click from
          // bubbling to the outer box's generic handler, so the specific field that was
          // clicked (not just "the first empty field") gets focused once active.
          <div className="EditGroup">
            {accountNameRequired && (
              <BlockRow>
                <BlockField label={t("form_account_name")}>
                  <div className="input" onClick={activateField("accountName")}>{accountName}</div>
                </BlockField>
              </BlockRow>
            )}

            <BlockRow>
              <BlockField label={t("form_name")}>
                <div className="input" onClick={activateField("name")}>{displayName}</div>
              </BlockField>
            </BlockRow>

            <BlockRow>
              <BlockField label={t("form_email")}>
                <div className="input" onClick={activateField("email")}>{displayEmail}</div>
              </BlockField>
            </BlockRow>

            <BlockRow>
              <BlockField label={t("form_password")}>
                <span className="password-field">
                  <div className="input" onClick={activateField("password")} />
                </span>
              </BlockField>
            </BlockRow>

            <BlockRow className="row-right block-row-top0">
              {/* A real (disabled) <button>, not a plain <div> like the other
                  placeholders: browser UA styling only renders the "disabled" look
                  (dimmed background/border) on an actual disabled form control, and this
                  box's real button would be disabled right now too (`!validInput`) --
                  no click handler needed since `type="button"` plus the outer box's own
                  onClick already covers "click anywhere to activate". */}
              <button className="button submit-button" disabled={!validInput || loading} type="button">{t("form_submit_signup")}</button>
            </BlockRow>
          </div>
        )}
      </Block>
    </div>
  );
}
